import React, { useState } from "react";
import SectionTag from "../components/SectionTag";
import ScrollReveal from "../components/ScrollReveal";
import {
  FiMail,
  FiPhone,
  FiClock /*, FiFacebook, FiInstagram, FiLinkedin, FiTwitter*/,
} from "react-icons/fi";
import { BASE_URL } from "../constants/BaseUrl";

const SUBJECTS = [
  "General Inquiry",
  "Partnership Opportunity",
  "Non-Profit Registration",
  "Technical Support",
  "Press & Media",
  "Other",
];

function ContactInfo({ icon, label, value }) {
  return (
    <div className="flex items-center gap-4">
      <div
        style={{
          width: "44px",
          height: "44px",
          background: "rgba(63,196,227,0.1)",
          borderRadius: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#3FC4E3",
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div>
        <div
          style={{
            fontFamily: "popinsMedium",
            fontSize: "15px",
            color: "#1B1A28",
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontFamily: "popinsRegular",
            fontSize: "14px",
            color: "#6B7280",
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

/* function SocialBtn({ icon }) {
  return (
    <button
      className="flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer"
      style={{ width: '44px', height: '44px', border: '1px solid #E5E5E5', color: '#3FC4E3', background: 'transparent' }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(63,196,227,0.08)'; e.currentTarget.style.borderColor = 'rgba(63,196,227,0.4)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#E5E5E5'; }}
    >
      {icon}
    </button>
  );
} */

const baseInput = {
  width: "100%",
  background: "#F8FAFC",
  border: "1px solid #E5E5E5",
  borderRadius: "10px",
  padding: "13px 16px",
  color: "#1B1A28",
  fontFamily: "popinsRegular",
  fontSize: "14px",
  outline: "none",
  transition: "border 300ms ease, box-shadow 300ms ease",
};

function Field({ label, error, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label
        style={{
          fontFamily: "popinsMedium",
          fontSize: "14px",
          color: "#1B1A28",
        }}
      >
        {label}
      </label>
      {children}
      {error && (
        <span
          style={{
            fontFamily: "popinsRegular",
            fontSize: "12px",
            color: "#FE5C73",
          }}
        >
          {error}
        </span>
      )}
    </div>
  );
}

function InputField({ type = "text", placeholder, value, onChange, error }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        ...baseInput,
        ...(focused
          ? {
              border: "1px solid #3FC4E3",
              boxShadow: "0 0 0 3px rgba(63,196,227,0.1)",
            }
          : {}),
        ...(error
          ? {
              border: "1px solid #FE5C73",
              boxShadow: "0 0 0 3px rgba(254,92,115,0.08)",
            }
          : {}),
      }}
    />
  );
}

function SelectField({ value, onChange, error }) {
  const [focused, setFocused] = useState(false);
  return (
    <select
      value={value}
      onChange={onChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        ...baseInput,
        cursor: "pointer",
        ...(focused
          ? {
              border: "1px solid #3FC4E3",
              boxShadow: "0 0 0 3px rgba(63,196,227,0.1)",
            }
          : {}),
        ...(error
          ? {
              border: "1px solid #FE5C73",
              boxShadow: "0 0 0 3px rgba(254,92,115,0.08)",
            }
          : {}),
      }}
    >
      <option value="">Select a subject</option>
      {SUBJECTS.map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
    </select>
  );
}

function TextareaField({ placeholder, value, onChange, error }) {
  const [focused, setFocused] = useState(false);
  return (
    <textarea
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      rows={5}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        ...baseInput,
        minHeight: "120px",
        resize: "vertical",
        ...(focused
          ? {
              border: "1px solid #3FC4E3",
              boxShadow: "0 0 0 3px rgba(63,196,227,0.1)",
            }
          : {}),
        ...(error
          ? {
              border: "1px solid #FE5C73",
              boxShadow: "0 0 0 3px rgba(254,92,115,0.08)",
            }
          : {}),
      }}
    />
  );
}

export default function Contact() {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = "Full name is required.";
    else if (form.fullName.trim().length > 200)
      e.fullName = "Full name must be 200 characters or less.";
    if (!form.email.trim()) e.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email address.";
    if (!form.subject) e.subject = "Please select a subject.";
    else if (form.subject.length > 300)
      e.subject = "Subject must be 300 characters or less.";
    if (!form.message.trim()) e.message = "Message is required.";
    return e;
  };

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    if (errors[field]) setErrors((er) => ({ ...er, [field]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setLoading(true);
    setApiError("");

    try {
      const response = await fetch(`${BASE_URL}website-queries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: form.fullName,
          email: form.email,
          subject: form.subject,
          message: form.message,
        }),
      });

      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

      setSubmitted(true);
    } catch (err) {
      setApiError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      style={{ background: "#ffffff", padding: "120px 5%" }}
    >
      <div className="max-w-screen-xl mx-auto">
        <div className="flex flex-col items-center text-center mb-16">
          <SectionTag label="CONTACT US" />
          <h2
            style={{
              fontFamily: "popinsBold",
              fontSize: "clamp(26px, 4vw, 44px)",
              color: "#1B1A28",
              marginBottom: "12px",
            }}
          >
            Let's Start a Conversation
          </h2>
          <p
            style={{
              fontFamily: "popinsRegular",
              fontSize: "16px",
              color: "#6B7280",
            }}
          >
            Have questions about TCCplus, interested in a partnership, or need
            support? We'd love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left */}
          <ScrollReveal direction="left">
            <h3
              style={{
                fontFamily: "popinsBold",
                fontSize: "clamp(24px, 3vw, 40px)",
                color: "#1B1A28",
                marginBottom: "20px",
              }}
            >
              Get In Touch
            </h3>
            <p
              style={{
                fontFamily: "popinsRegular",
                fontSize: "16px",
                color: "#6B7280",
                lineHeight: "1.8",
                marginBottom: "32px",
              }}
            >
              Whether you're a non-profit looking to join our platform or an
              individual seeking support, we're here to help.
            </p>
            <div className="flex flex-col gap-6 mb-10">
              <ContactInfo
                icon={<FiMail size={20} />}
                label="Email"
                value="info@countyconnectplus.com"
              />
              <ContactInfo
                icon={<FiPhone size={20} />}
                label="Phone"
                value="+1 (940) 594-3419"
              />
              <ContactInfo
                icon={<FiClock size={20} />}
                label="Response Time"
                value="Within 24 hours"
              />
            </div>
            {/* <div className="flex gap-3">
              <SocialBtn icon={<FiFacebook size={18} />} />
              <SocialBtn icon={<FiInstagram size={18} />} />
              <SocialBtn icon={<FiLinkedin size={18} />} />
              <SocialBtn icon={<FiTwitter size={18} />} />
            </div> */}
          </ScrollReveal>

          {/* Right — form */}
          <ScrollReveal direction="right">
            <div
              style={{
                background: "#ffffff",
                border: "1px solid #E5E5E5",
                borderRadius: "20px",
                padding: "40px",
                boxShadow: "0 8px 40px rgba(0,0,0,0.06)",
              }}
            >
              {submitted ? (
                <div className="flex flex-col items-center text-center gap-4 py-8">
                  <div
                    style={{
                      width: "64px",
                      height: "64px",
                      borderRadius: "50%",
                      background: "rgba(63,196,227,0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "28px",
                      color: "#3FC4E3",
                    }}
                  >
                    ✓
                  </div>
                  <h4
                    style={{
                      fontFamily: "popinsSemiBold",
                      fontSize: "22px",
                      color: "#1B1A28",
                    }}
                  >
                    Message Sent!
                  </h4>
                  <p
                    style={{
                      fontFamily: "popinsRegular",
                      fontSize: "15px",
                      color: "#6B7280",
                    }}
                  >
                    Thank you for reaching out. We'll get back to you within 24
                    hours.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  noValidate
                  className="flex flex-col gap-5"
                >
                  <Field label="Full Name" error={errors.fullName}>
                    <InputField
                      type="text"
                      placeholder="Your full name"
                      value={form.fullName}
                      onChange={handleChange("fullName")}
                      error={errors.fullName}
                    />
                  </Field>
                  <Field label="Email Address" error={errors.email}>
                    <InputField
                      type="email"
                      placeholder="your@email.com"
                      value={form.email}
                      onChange={handleChange("email")}
                      error={errors.email}
                    />
                  </Field>
                  <Field label="Subject" error={errors.subject}>
                    <SelectField
                      value={form.subject}
                      onChange={handleChange("subject")}
                      error={errors.subject}
                    />
                  </Field>
                  <Field label="Message" error={errors.message}>
                    <TextareaField
                      placeholder="Tell us how we can help..."
                      value={form.message}
                      onChange={handleChange("message")}
                      error={errors.message}
                    />
                  </Field>
                  {apiError && (
                    <span style={{ fontFamily: "popinsRegular", fontSize: "13px", color: "#FE5C73" }}>
                      {apiError}
                    </span>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    className="transition-all duration-300 mt-2"
                    style={{
                      width: "100%",
                      height: "52px",
                      borderRadius: "10px",
                      background: loading
                        ? "rgba(63,196,227,0.5)"
                        : "linear-gradient(135deg, #3FC4E3 0%, #006077 100%)",
                      color: "#ffffff",
                      fontFamily: "popinsSemiBold",
                      fontSize: "16px",
                      border: "none",
                      boxShadow: "0 4px 16px rgba(63,196,227,0.3)",
                      cursor: loading ? "not-allowed" : "pointer",
                    }}
                    onMouseEnter={(e) => {
                      if (loading) return;
                      e.currentTarget.style.boxShadow = "0 8px 28px rgba(63,196,227,0.5)";
                      e.currentTarget.style.transform = "scale(1.02)";
                    }}
                    onMouseLeave={(e) => {
                      if (loading) return;
                      e.currentTarget.style.boxShadow = "0 4px 16px rgba(63,196,227,0.3)";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    {loading ? "Sending..." : "Send Message"}
                  </button>
                </form>
              )}
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
